import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, CalendarDays, Package, Truck, Search, Download, ChevronDown, ChevronUp, X as XIcon, RefreshCw, Bug, Copy } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* Convert an input (Date or 'YYYY-MM-DD') to Monday of that ISO week (local, no UTC shift). */
function mondayOf(d) {
  let dt;
  if (typeof d === 'string' && d.length === 10) {
    dt = new Date(d + 'T12:00:00'); // noon local — safe against timezone edge cases
  } else {
    dt = new Date(d);
    dt.setHours(12, 0, 0, 0);
  }
  const day = dt.getDay(); // 0=Sun ... 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const da = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

/* Add N days to a 'YYYY-MM-DD' string, returning a 'YYYY-MM-DD' string (local time). */
function addDays(iso, n) {
  const dt = new Date(iso + 'T12:00:00');
  dt.setDate(dt.getDate() + n);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const da = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
}

function formatDayLabel(iso) {
  const dt = new Date(iso + 'T00:00:00');
  return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}


export default function WeeklyPlan({ initialWeekStart = null, embedded = false }) {
  const [weekStart, setWeekStart] = useState(() => initialWeekStart ? mondayOf(initialWeekStart) : mondayOf(new Date()));
  // date_field is fixed to 'ship' — user asked to remove the toggle (production/prep = dock - 5d)
  const dateField = 'ship';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [vendorFilter, setVendorFilter] = useState('');
  const [excludedVendors, setExcludedVendors] = useState(() => new Set());
  const [excludedProjects, setExcludedProjects] = useState(() => new Set());
  const [searchQ, setSearchQ] = useState('');
  const [collapsed, setCollapsed] = useState(embedded); // collapsed by default when embedded
  const [showDebug, setShowDebug] = useState(false);

  // Sync week from parent if it changes
  useEffect(() => {
    if (initialWeekStart) setWeekStart(mondayOf(initialWeekStart));
  }, [initialWeekStart]);

  const fetchData = useCallback(async () => {
    if (embedded && collapsed) return; // skip fetch while collapsed to save load
    setLoading(true);
    try {
      const excludeParam = Array.from(excludedVendors).join(',');
      const excludeProjectsParam = Array.from(excludedProjects).join(',');
      const res = await axios.get(`${API}/api/planning/city-bus-weekly`, {
        params: {
          week_start: weekStart,
          date_field: dateField,
          exclude_vendors: excludeParam,
          exclude_projects: excludeProjectsParam,
        },
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load weekly plan: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [weekStart, excludedVendors, excludedProjects, embedded, collapsed]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const shiftWeek = (delta) => setWeekStart(w => addDays(w, delta * 7));

  const toggleExcludeVendor = (v) => {
    setExcludedVendors(prev => {
      const next = new Set(prev);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });
    if (vendorFilter) setVendorFilter('');
  };

  const toggleExcludeProject = (pid) => {
    setExcludedProjects(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const filteredMaterials = useMemo(() => {
    if (!data?.materials) return [];
    const q = (searchQ || '').trim().toLowerCase();
    return data.materials.filter(m => {
      if (vendorFilter && m.vendor !== vendorFilter) return false;
      if (q && !`${m.item_code} ${m.description}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, vendorFilter, searchQ]);

  const totals = useMemo(() => {
    if (!data?.materials) return { items: 0, byUom: [], shortCount: 0 };
    const byUom = {};
    let shortCount = 0;
    filteredMaterials.forEach(m => {
      const u = (m.uom || '').trim() || '—';
      byUom[u] = (byUom[u] || 0) + (m.total_required || 0);
      if ((m.total_short || 0) > 0) shortCount += 1;
    });
    const list = Object.entries(byUom)
      .sort((a, b) => b[1] - a[1])
      .map(([uom, total]) => ({ uom, total: Math.round(total * 100) / 100 }));
    return { items: filteredMaterials.length, byUom: list, shortCount };
  }, [data, filteredMaterials]);

  const debugPayload = useMemo(() => {
    if (!data) return null;
    return {
      week: data.week,
      total_sets: data.total_sets,
      material_count: data.material_count,
      warnings: data.warnings || {},
      sets: data.diagnostics?.sets || [],
      projects_in_week: (data.projects_in_week || []).map(p => ({ sr: p.sr_number, name: p.project_name, sets: p.set_count })),
    };
  }, [data]);

  const copyDebug = () => {
    const text = JSON.stringify(debugPayload, null, 2);
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Debug data copied — paste it in the chat');
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => toast.success('Debug data copied — paste it in the chat'),
        fallback
      );
    } else fallback();
  };

  const exportExcel = async () => {
    try {
      const excludeParam = Array.from(excludedVendors).join(',');
      const excludeProjectsParam = Array.from(excludedProjects).join(',');
      const res = await axios.get(`${API}/api/planning/city-bus-weekly/export`, {
        params: {
          week_start: weekStart,
          date_field: dateField,
          exclude_vendors: excludeParam,
          exclude_projects: excludeProjectsParam,
        },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `city-bus-weekly-plan_${weekStart}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (e) {
      toast.error('Export failed: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="space-y-4" data-testid="weekly-plan-report">
      {/* Header controls */}
      <div className="flex items-center justify-between gap-3 flex-wrap bg-white border border-slate-200 rounded-xl p-3">
        {!embedded ? (
          <div className="flex items-center gap-2">
            <button onClick={() => shiftWeek(-1)}
              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
              data-testid="week-prev-btn"><ChevronLeft className="w-4 h-4 text-slate-700" /></button>
            <div className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 text-cyan-600" />
              <input type="date" value={weekStart}
                onChange={(e) => setWeekStart(mondayOf(e.target.value))}
                className="border border-slate-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-cyan-400"
                data-testid="week-start-input" />
              <span className="text-xs text-slate-500 font-semibold">
                → {data?.week?.end || addDays(weekStart, 6)}
              </span>
            </div>
            <button onClick={() => shiftWeek(1)}
              className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors"
              data-testid="week-next-btn"><ChevronRight className="w-4 h-4 text-slate-700" /></button>
            <button onClick={() => setWeekStart(mondayOf(new Date()))}
              className="text-[10px] px-2 py-1 rounded-md bg-cyan-600 text-white font-semibold hover:bg-cyan-700"
              data-testid="week-today-btn">This week</button>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm cursor-pointer select-none" onClick={() => setCollapsed(c => !c)}
            data-testid="wp-collapse-toggle">
            {collapsed ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronUp className="w-4 h-4 text-slate-500" />}
            <Package className="w-4 h-4 text-emerald-600" />
            <span className="font-bold text-slate-800">Consolidated Material Demand</span>
            <span className="text-[9px] font-bold text-slate-400 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">City Bus + Coach</span>
            <span className="text-xs text-slate-500">({weekStart} → {data?.week?.end || addDays(weekStart, 6)})</span>
            {data && !collapsed && (
              <span className="text-[10px] text-slate-400 ml-2">
                {data.total_sets} sets · {data.material_count} materials
              </span>
            )}
          </div>
        )}

        {!collapsed && (
        <div className="flex items-center gap-2">
          <button onClick={() => fetchData()} disabled={loading}
            className="p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors disabled:opacity-50"
            title="Refresh — pull latest production plan changes"
            data-testid="wp-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setShowDebug(true)} disabled={loading || !data}
            className="p-1.5 rounded-md bg-slate-100 hover:bg-amber-100 text-slate-600 hover:text-amber-700 transition-colors disabled:opacity-50"
            title="Debug — show raw calculation data (copy & send for support)"
            data-testid="wp-debug-btn">
            <Bug className="w-3.5 h-3.5" />
          </button>
          <button onClick={exportExcel} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-md hover:bg-emerald-700 disabled:opacity-50"
            data-testid="export-xlsx-btn">
            <Download className="w-3.5 h-3.5" /> Excel
          </button>
        </div>
        )}
      </div>

      {!collapsed && (
      <>
      {loading && <div className="text-center py-10 text-slate-400 text-sm">Loading weekly plan…</div>}

      {!loading && data && (
        <>
          {/* Projects in this week (with hide toggle) */}
          {(data.projects_in_week?.length > 0 || excludedProjects.size > 0) && (
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-cyan-600" /> Projects included this week
                  <span className="text-[10px] font-normal text-slate-400">Click a chip to hide that project's demand</span>
                </div>
                {excludedProjects.size > 0 && (
                  <button onClick={() => setExcludedProjects(new Set())}
                    className="text-[10px] text-red-600 font-semibold hover:text-red-800 underline"
                    data-testid="clear-excluded-projects">
                    Show all ({excludedProjects.size} hidden)
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.projects_in_week?.map(p => {
                  const isCoach = p.category === 'Coach';
                  return (
                  <button key={p.project_id}
                    onClick={() => toggleExcludeProject(p.project_id)}
                    className={`text-[10px] px-2.5 py-1 rounded-md border font-semibold flex items-center gap-1 transition-colors ${isCoach ? 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100' : 'bg-cyan-50 border-cyan-200 text-cyan-700 hover:bg-cyan-100'}`}
                    title={`Click to hide ${p.project_name}`}
                    data-testid={`project-chip-${p.sr_number}`}>
                    <input type="checkbox" checked readOnly className={`w-3 h-3 ${isCoach ? 'accent-amber-600' : 'accent-cyan-600'}`} />
                    <span className="font-mono">{p.sr_number}</span>
                    <span className={`font-normal ${isCoach ? 'text-amber-500' : 'text-cyan-500'}`}>{p.project_name}</span>
                    {isCoach && <span className="text-[8px] font-bold uppercase bg-amber-100 border border-amber-200 rounded px-1">Coach</span>}
                    <span className={`text-[9px] ml-0.5 ${isCoach ? 'text-amber-400' : 'text-cyan-400'}`}>({p.set_count} sets)</span>
                  </button>
                  );
                })}
                {Array.from(excludedProjects).map(pid => (
                  <button key={pid}
                    onClick={() => toggleExcludeProject(pid)}
                    className="text-[10px] px-2.5 py-1 rounded-md border font-semibold flex items-center gap-1 bg-slate-100 border-slate-200 text-slate-400 line-through hover:bg-slate-200"
                    title="Click to include again"
                    data-testid={`project-chip-excluded-${pid}`}>
                    <input type="checkbox" readOnly className="w-3 h-3" />
                    <span className="font-mono">{pid.slice(-6)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {loading && <div className="text-center py-10 text-slate-400 text-sm">Loading weekly plan…</div>}

          {/* Missing CRR warning — variant/primary BOM not uploaded for one or more sets */}
          {data.warnings?.missing_crr?.length > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-xl p-3" data-testid="missing-crr-warning">
              <div className="flex items-start gap-2">
                <div className="text-amber-600 text-lg leading-none">⚠</div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-amber-800">
                    {data.warnings.missing_crr.reduce((n, w) => n + (w.affected_sets?.length || 0), 0)} sets have no CRR (BOM) uploaded — their materials are not included
                  </div>
                  <div className="text-[11px] text-amber-700 mt-0.5">
                    Upload the missing variant CRRs from <b>Projects → Select Project → Upload CRR</b> to include these sets.
                  </div>
                  <div className="mt-2 flex flex-col gap-1">
                    {data.warnings.missing_crr.map((w, i) => (
                      <div key={i} className="flex items-center gap-2 flex-wrap text-[11px]">
                        <span className="font-mono font-bold text-amber-900 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">{w.sr_number || '—'}</span>
                        <span className="text-amber-800">{w.project_name}</span>
                        <span className="text-amber-500">·</span>
                        <span className="text-amber-700">Missing variant:</span>
                        <span className="font-mono font-bold text-amber-900 bg-white border border-amber-300 rounded px-1.5 py-0.5">
                          {w.nfi_code || '(Primary)'}
                        </span>
                        <span className="text-amber-500">·</span>
                        <span className="text-amber-700">Sets:</span>
                        <span className="text-amber-900 font-mono">
                          {(w.affected_sets || []).map(s => `#${s.set_number}`).join(', ')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Empty CRR warning — CRR exists but has 0 usable items (bad upload / parse) */}
          {data.warnings?.empty_crr?.length > 0 && (
            <div className="bg-red-50 border border-red-300 rounded-xl p-3" data-testid="empty-crr-warning">
              <div className="flex items-start gap-2">
                <div className="text-red-600 text-lg leading-none">⚠</div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-red-800">
                    {data.warnings.empty_crr.reduce((n, w) => n + (w.affected_sets?.length || 0), 0)} sets reference a CRR that contains no usable material rows
                  </div>
                  <div className="text-[11px] text-red-700 mt-0.5">
                    The CRR file was uploaded but has 0 valid items (missing item codes or quantities). Re-upload the CRR from <b>Projects → Select Project → Upload CRR</b>.
                  </div>
                  <div className="mt-2 flex flex-col gap-1">
                    {data.warnings.empty_crr.map((w, i) => (
                      <div key={i} className="flex items-center gap-2 flex-wrap text-[11px]">
                        <span className="font-mono font-bold text-red-900 bg-red-100 border border-red-300 rounded px-1.5 py-0.5">{w.sr_number || '—'}</span>
                        <span className="text-red-800">{w.project_name}</span>
                        <span className="text-red-500">·</span>
                        <span className="text-red-700">Variant:</span>
                        <span className="font-mono font-bold text-red-900 bg-white border border-red-300 rounded px-1.5 py-0.5">{w.nfi_code || '(Primary)'}</span>
                        <span className="text-red-500">·</span>
                        <span className="text-red-700">Rows in file: {w.item_count}</span>
                        <span className="text-red-500">·</span>
                        <span className="text-red-700">Sets:</span>
                        <span className="text-red-900 font-mono">{(w.affected_sets || []).map(s => `#${s.set_number}`).join(', ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Vendor summary strip — click to include-filter, right-click to toggle exclude */}
          {data.vendor_summary?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  <Package className="w-4 h-4 text-indigo-600" /> Vendors this week
                  <span className="text-[10px] font-normal text-slate-400" title="Vendor comes from Sage Component DB (item_code → vendor). Fallback: CRR Excel column D. Known spelling variants (e.g. 'KIEL POLAND SP. ZO.O.' → 'Kiel Polska Sp. z o.o.') are auto-merged. To edit source: Sage Tools → Component DB.">
                    ⓘ Source: Sage Component DB · aliases auto-merged
                  </span>
                  <span className="text-[10px] font-normal text-slate-400">· Click to filter · Ctrl-click to hide</span>
                </div>
                <div className="flex items-center gap-2">
                  {excludedVendors.size > 0 && (
                    <span className="text-[10px] text-red-600 font-semibold">
                      Hiding {excludedVendors.size} vendor{excludedVendors.size !== 1 && 's'}
                      <button className="ml-1 underline hover:text-red-800"
                        onClick={() => setExcludedVendors(new Set())}
                        data-testid="clear-exclude-btn">Reset</button>
                    </span>
                  )}
                  {vendorFilter && (
                    <button onClick={() => setVendorFilter('')}
                      className="text-[10px] text-slate-500 hover:text-slate-700 underline">Clear include filter</button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.vendor_summary.map(v => {
                  const active = vendorFilter === v.vendor;
                  const excluded = excludedVendors.has(v.vendor);
                  return (
                    <button key={v.vendor}
                      onClick={(e) => {
                        if (e.ctrlKey || e.metaKey || e.shiftKey) {
                          toggleExcludeVendor(v.vendor);
                        } else {
                          setVendorFilter(active ? '' : v.vendor);
                        }
                      }}
                      onContextMenu={(e) => { e.preventDefault(); toggleExcludeVendor(v.vendor); }}
                      className={`text-[10px] px-2.5 py-1 rounded-md border font-semibold transition-colors flex items-center gap-1 ${
                        excluded ? 'bg-red-100 border-red-300 text-red-700 line-through opacity-70'
                        : active ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100'
                      }`}
                      title={excluded ? 'Excluded — click to re-include' : 'Ctrl/right-click to hide from list'}
                      data-testid={`vendor-chip-${v.vendor}`}>
                      {excluded && <XIcon className="w-3 h-3" />}
                      {v.vendor} <span className="ml-1 text-[9px] opacity-80">({v.items})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Materials table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50">
              <div className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <Package className="w-4 h-4 text-emerald-600" /> Consolidated Material Demand
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
                  <input type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="Search code / description…"
                    className="border border-slate-200 rounded-md pl-7 pr-2 py-1 text-xs w-56 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                    data-testid="material-search" />
                </div>
                <div className="text-[10px] text-slate-500 flex items-center gap-1 flex-wrap">
                  <span className="font-bold text-slate-700">{totals.items}</span> items
                  {totals.shortCount > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 font-bold">
                      {totals.shortCount} short
                    </span>
                  )}
                  {totals.byUom.length > 0 && <span className="mx-1 text-slate-300">·</span>}
                  {totals.byUom.map((t, i) => (
                    <span key={t.uom} className="flex items-center gap-0.5">
                      {i > 0 && <span className="text-slate-300 mx-0.5">·</span>}
                      <span className="font-semibold text-emerald-700">{t.total.toLocaleString()}</span>
                      <span className="text-[9px] font-bold text-emerald-500">{t.uom}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-white z-10 shadow-sm">
                  <tr className="text-slate-400 uppercase tracking-wider text-[9px] border-b border-slate-200">
                    <th className="px-3 py-2 text-left font-semibold">Item Code</th>
                    <th className="px-3 py-2 text-left font-semibold">Description</th>
                    <th className="px-3 py-2 text-left font-semibold">Vendor</th>
                    <th className="px-3 py-2 text-left font-semibold">Group</th>
                    <th className="px-3 py-2 text-left font-semibold">UOM</th>
                    <th className="px-3 py-2 text-right font-semibold">Total Qty</th>
                    <th className="px-2 py-2 text-right font-semibold text-indigo-500">KSU</th>
                    <th className="px-2 py-2 text-right font-semibold text-orange-500">Kiel</th>
                    <th className="px-2 py-2 text-right font-semibold">Stock</th>
                    <th className="px-2 py-2 text-right font-semibold text-red-400">Short</th>
                    <th className="px-3 py-2 text-left font-semibold">Set Breakdown (who needs how much)</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMaterials.length === 0 && (
                    <tr><td colSpan={11} className="text-center py-8 text-slate-400 italic">No materials for the selected week / filter.</td></tr>
                  )}
                  {filteredMaterials.map((m, i) => {
                    const isOpen = expandedItem === m.item_code;
                    const isShort = (m.total_short || 0) > 0;
                    return (
                      <React.Fragment key={m.item_code}>
                        <tr className={`border-t border-slate-100 hover:bg-emerald-50/40 ${isShort ? 'bg-red-50/60' : (i % 2 ? 'bg-slate-50/30' : '')}`}>
                          <td className="px-3 py-1.5 font-mono font-bold text-slate-700">{m.item_code}</td>
                          <td className="px-3 py-1.5 text-slate-600 max-w-[250px]" title={m.description}>{m.description}</td>
                          <td className="px-3 py-1.5">
                            {m.vendor ? (
                              <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                                {m.vendor}
                              </span>
                            ) : <span className="text-slate-300">-</span>}
                          </td>
                          <td className="px-3 py-1.5 text-slate-500">{m.group || '-'}</td>
                          <td className="px-3 py-1.5 text-slate-500">{m.uom || '-'}</td>
                          <td className="px-3 py-1.5 text-right font-black text-emerald-700">{m.total_required}</td>
                          <td className="px-2 py-1.5 text-right text-indigo-600 font-semibold" title={`KSU On Hand: ${m.ksu_on_hand || 0}  ·  On PO: ${m.ksu_on_po || 0}  ·  On SO: ${m.ksu_on_so || 0}`}>{m.ksu_available || 0}</td>
                          <td className="px-2 py-1.5 text-right text-orange-600 font-semibold" title={`Kiel On Hand: ${m.kiel_on_hand || 0}  ·  On PO: ${m.kiel_on_po || 0}  ·  On SO: ${m.kiel_on_so || 0}`}>{m.kiel_available || 0}</td>
                          <td className={`px-2 py-1.5 text-right font-bold ${(m.total_stock || 0) >= (m.total_required || 0) && (m.total_required || 0) > 0 ? 'text-emerald-700' : 'text-slate-700'}`}>{m.total_stock || 0}</td>
                          <td className={`px-2 py-1.5 text-right font-bold ${isShort ? 'text-red-600' : 'text-slate-300'}`}>{m.total_short || 0}</td>
                          <td className="px-3 py-1.5">
                            <button
                              onClick={() => setExpandedItem(isOpen ? null : m.item_code)}
                              className="text-left w-full hover:bg-slate-100 rounded px-1 py-0.5 transition-colors"
                              data-testid={`breakdown-toggle-${m.item_code}`}>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {(m.sets_breakdown || []).slice(0, isOpen ? m.sets_breakdown.length : 4).map((b, bi) => (
                                  <span key={bi}
                                    className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700"
                                    title={`${b.project_name} · ${b.nfi_label}`}>
                                    <span className="text-slate-500">{b.sr_number}</span>
                                    <span className="text-cyan-700 font-bold ml-0.5">#{b.set_number ?? '—'}</span>
                                    <span className="text-slate-400 mx-1">·</span>
                                    <span className="text-slate-500">{b.day_label}</span>
                                    <span className="text-slate-400 mx-1">·</span>
                                    <span className="font-black text-emerald-700">{b.qty}</span>
                                  </span>
                                ))}
                                {!isOpen && m.sets_breakdown.length > 4 && (
                                  <span className="text-[10px] text-slate-400 italic">+{m.sets_breakdown.length - 4} more…</span>
                                )}
                              </div>
                            </button>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      </>
      )}

      {/* Debug modal — raw calculation data with one-click copy */}
      {showDebug && debugPayload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDebug(false)} data-testid="wp-debug-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Bug className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-bold text-slate-800">Weekly Plan — Debug Data</span>
                <span className="text-[10px] text-slate-400">({weekStart} → {data?.week?.end})</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={copyDebug}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-md hover:bg-amber-600"
                  data-testid="wp-debug-copy-btn">
                  <Copy className="w-3.5 h-3.5" /> Copy All
                </button>
                <button onClick={() => setShowDebug(false)}
                  className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                  data-testid="wp-debug-close-btn">
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="px-4 py-2 text-[11px] text-slate-500 bg-amber-50 border-b border-amber-100">
              Click <b>Copy All</b> and paste the text in the support chat — it shows exactly which CRR variant each set matched and how many items it produced.
            </div>
            <pre className="flex-1 overflow-auto p-4 text-[11px] font-mono text-slate-700 bg-slate-50 rounded-b-xl whitespace-pre-wrap" data-testid="wp-debug-json">
{JSON.stringify(debugPayload, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
